import firebase from "firebase/compat/app"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  BOTS_ENABLED,
  EloRankThreshold,
  MAX_PLAYERS_PER_GAME
} from "../../../../../config"
import type { IGameUser } from "../../../../../models/colyseus-models/game-user"
import { Role } from "../../../../../types"
import { EloRank } from "../../../../../types/enum/EloRank"
import { BotDifficulty, GameMode } from "../../../../../types/enum/Game"
import { SpecialGameRule } from "../../../../../types/enum/SpecialGameRule"
import { formatMinMaxRanks } from "../../../../../utils/elo"
import { throttle } from "../../../../../utils/function"
import { max } from "../../../../../utils/number"
import { keys } from "../../../../../utils/object"
import { setTitleNotificationIcon } from "../../../../../utils/window"
import { useAppSelector } from "../../../hooks"
import {
  addBot,
  changeRoomMinMaxRanks,
  changeRoomName,
  changeRoomPassword,
  gameStartRequest,
  rooms,
  setNoElo,
  setSpecialRule,
  toggleReady
} from "../../../network"
import { cc } from "../../utils/jsx"
import { GameModeIcon } from "../icons/game-mode-icon"
import { BotSelectModal } from "./bot-select-modal"
import PreparationMenuUser from "./preparation-menu-user"
import "./preparation-menu.css"

export default function PreparationMenu() {
  const { t } = useTranslation()
  const [inputValue, setInputValue] = useState<string>("")
  const users: IGameUser[] = useAppSelector((state) => state.preparation.users)
  const user = useAppSelector((state) => state.preparation.user)
  const name: string = useAppSelector((state) => state.preparation.name)
  const ownerId: string = useAppSelector((state) => state.preparation.ownerId)
  const password: string | null = useAppSelector(
    (state) => state.preparation.password
  )
  const noElo: boolean = useAppSelector((state) => state.preparation.noElo)
  const specialGameRule: SpecialGameRule | null = useAppSelector(
    (state) => state.preparation.specialGameRule
  )
  const minRank = useAppSelector((state) => state.preparation.minRank)
  const maxRank = useAppSelector((state) => state.preparation.maxRank)
  const [showBotSelectModal, setShowBotSelectModal] = useState(false)
  const uid: string = useAppSelector((state) => state.network.uid)
  const isOwner: boolean = useAppSelector(
    (state) => state.preparation.ownerId === state.network.uid
  )

  const isAdmin = user?.role === Role.ADMIN
  const isModerator = user?.role === Role.MODERATOR

  const gameMode = useAppSelector((state) => state.preparation.gameMode)
  const hasBotsEnabled = gameMode === GameMode.CUSTOM_LOBBY || isAdmin
  const [botDifficulty, setBotDifficulty] = useState<BotDifficulty>(
    BotDifficulty.MEDIUM
  )

  const isReady = users.find((user) => user.uid === uid)?.ready
  const nbUsersReady = users.filter((user) => user.ready).length
  const allUsersReady = users.every((user) => user.ready) && nbUsersReady > 1

  const nbExpectedPlayers = useAppSelector((state) =>
    state.preparation.whitelist && state.preparation.whitelist.length > 0
      ? max(MAX_PLAYERS_PER_GAME)(state.preparation.whitelist.length)
      : MAX_PLAYERS_PER_GAME
  )

  useEffect(() => {
    if (allUsersReady) {
      setTitleNotificationIcon("🟢")
    } else if (nbUsersReady === 0) {
      setTitleNotificationIcon("🔴")
    } else if (nbUsersReady === users.length - 1) {
      setTitleNotificationIcon("🟡")
    } else {
      setTitleNotificationIcon("🟠")
    }
  }, [nbUsersReady, users.length, allUsersReady])

  const humans = users.filter((u) => !u.isBot)
  const isEligibleForELO = gameMode === GameMode.RANKED
  const averageElo =
    humans.length > 0
      ? Math.round(humans.reduce((acc, u) => acc + u.elo, 0) / humans.length)
      : 0

  function togglePrivate() {
    if (password === null || password === undefined) {
      // generate a random password made of 4 characters
      const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789"
      const randomBytes = new Uint8Array(4)
      crypto.getRandomValues(randomBytes)
      const newPassword = Array.from(
        randomBytes,
        (b) => chars[b % chars.length]
      ).join("")
      changeRoomPassword(newPassword)
    } else {
      changeRoomPassword(null)
    }
  }

  function toggleNoElo() {
    setNoElo(!noElo)
  }

  const startGame = throttle(async function startGame() {
    if (rooms.preparation) {
      const token = await firebase.auth().currentUser?.getIdToken()
      if (token) {
        gameStartRequest(token)
      }
    }
  }, 1000)

  const changeMinRank = (newMinRank: EloRank) => {
    changeRoomMinMaxRanks({
      minRank: newMinRank,
      maxRank: maxRank
    })
  }

  const changeMaxRank = (newMaxRank: EloRank) => {
    changeRoomMinMaxRanks({
      minRank: minRank,
      maxRank: newMaxRank
    })
  }

  const changeSpecialRule = (rule: SpecialGameRule | "none") => {
    setSpecialRule(rule === "none" ? null : rule)
  }

  const headerMessage = (
    <>
      {gameMode === GameMode.RANKED && (
        <p>
          <GameModeIcon gameMode={gameMode} />
          {t("ranked_game_hint")}
        </p>
      )}

      {(gameMode === GameMode.SCRIBBLE || specialGameRule != null) && (
        <p>
          <GameModeIcon gameMode={gameMode} />
          {t("smeargle_scribble_hint")}
        </p>
      )}

      {gameMode === GameMode.CLASSIC && (
        <p>
          <GameModeIcon gameMode={gameMode} />
          {t("classic_hint")}
        </p>
      )}

      {noElo === true ? (
        <p>
          <img
            alt={t("no_elo")}
            title={t("no_elo_hint")}
            className="noelo icon"
            src="/assets/ui/noelo.png"
          />
          {t("no_elo_hint")}
        </p>
      ) : isEligibleForELO ? (
        <p>
          {t("eligible_elo_hint")} {t("average_elo")}: {averageElo} ;{" "}
          {t("GLHF")}
          {" !"}
        </p>
      ) : users.length > 1 ? (
        <p>{t("not_eligible_elo_hint")}</p>
      ) : null}

      {hasBotsEnabled && users.length === 1 && (
        <p>
          {BOTS_ENABLED
            ? t("add_bot_or_wait_hint")
            : t("wait_for_players_hint")}
        </p>
      )}
    </>
  )

  const roomPrivateButton = gameMode === GameMode.CUSTOM_LOBBY &&
    (isOwner || isAdmin) && (
      <button
        className="bubbly blue"
        onClick={togglePrivate}
        title={
          password ? t("make_room_public_hint") : t("make_room_private_hint")
        }
      >
        {password ? t("make_room_public") : t("make_room_private")}
      </button>
    )

  const roomEloButton = gameMode === GameMode.CUSTOM_LOBBY && isAdmin && (
    <button
      className="bubbly blue"
      onClick={toggleNoElo}
      title={noElo ? t("enable_elo_hint") : t("disable_elo_hint")}
    >
      {noElo ? t("enable_elo") : t("disable_elo")}
    </button>
  )

  const minMaxRanks = gameMode === GameMode.CUSTOM_LOBBY && isOwner && (
    <>
      <RankSelect
        label={t("minimum_rank")}
        value={minRank ?? EloRank.LEVEL_BALL}
        onChange={changeMinRank}
      />
      <RankSelect
        label={t("maximum_rank")}
        value={maxRank ?? EloRank.BEAST_BALL}
        onChange={changeMaxRank}
      />
    </>
  )

  const scribbleRule = gameMode === GameMode.CUSTOM_LOBBY &&
    isAdmin &&
    noElo && (
      <label>
        {t("game_modes.SCRIBBLE")}
        <select
          onChange={(e) => changeSpecialRule(e.target.value as SpecialGameRule)}
          value={specialGameRule ?? "none"}
        >
          <option value="none">{t("no_rule")}</option>
          {keys(SpecialGameRule).map((rule) => (
            <option key={rule} value={rule}>
              {t(`scribble.${rule}`)}
            </option>
          ))}
        </select>
      </label>
    )

  const roomNameInput = gameMode === GameMode.CUSTOM_LOBBY &&
    (isModerator || isAdmin) &&
    user &&
    !user.anonymous && (
      <div className="my-input-group">
        <input
          maxLength={30}
          type="text"
          placeholder={name}
          style={{ flex: 1 }}
          onChange={(e) => {
            setInputValue(e.target.value)
          }}
          className="with-button"
        />
        <button
          className="bubbly blue"
          onClick={() => changeRoomName(inputValue)}
        >
          {t("change_room_name")}
        </button>
      </div>
    )

  const botControls = hasBotsEnabled && (isOwner || isAdmin) && (
    <div className="my-input-group">
      <button
        className="bubbly blue"
        onClick={() => {
          if (botDifficulty === BotDifficulty.CUSTOM) {
            setShowBotSelectModal(true)
          } else {
            addBot(botDifficulty)
          }
        }}
      >
        {t("add_bot")}
      </button>

      <select
        value={botDifficulty}
        onChange={(e) => {
          setBotDifficulty(e.target.value as BotDifficulty)
        }}
      >
        <option value={BotDifficulty.BEGINNER}>
          {t("bot_difficulty.BEGINNER")}
        </option>
        <option value={BotDifficulty.EASY}>{t("bot_difficulty.EASY")}</option>
        <option value={BotDifficulty.MEDIUM}>
          {t("bot_difficulty.MEDIUM")}
        </option>
        <option value={BotDifficulty.HARD}>{t("bot_difficulty.HARD")}</option>
        <option value={BotDifficulty.EXTREME}>
          {t("bot_difficulty.EXTREME")}
        </option>
        <option value={BotDifficulty.MASTER}>
          {t("bot_difficulty.MASTER")}
        </option>
        <option value={BotDifficulty.CUSTOM}>
          {t("bot_difficulty.CUSTOM")}
        </option>
      </select>
    </div>
  )

  const roomInfo = gameMode === GameMode.CUSTOM_LOBBY && (
    <p className="room-info">
      {password && (
        <>
          {t("room_password")}: <b>{password}</b>
        </>
      )}
    </p>
  )

  const readyButton = (gameMode === GameMode.CUSTOM_LOBBY ||
    gameMode === GameMode.DOUBLE_UP ||
    !isReady) &&
    users.length > 0 && (
      <button
        className={cc("bubbly", "ready-button", isReady ? "green" : "orange")}
        onClick={() => {
          toggleReady(!isReady)
        }}
      >
        {t("ready")} {isReady ? "✔" : "?"}
      </button>
    )

  const startGameButton = (isOwner || isAdmin) && (
    <button
      className={cc("bubbly", {
        green: allUsersReady,
        orange: !allUsersReady
      })}
      onClick={startGame}
      data-tooltip-id={"start-game"}
    >
      {t("start_game")}
    </button>
  )

  return (
    <div className="preparation-menu my-container is-centered custom-bg">
      <header>
        <h1>
          {formatMinMaxRanks(minRank, maxRank)} {name}: {users.length}/
          {nbExpectedPlayers}
        </h1>
        {headerMessage}
      </header>

      <div
        className={`preparation-menu-users${gameMode === GameMode.DOUBLE_UP ? " double-up" : ""}`}
      >
        {gameMode === GameMode.DOUBLE_UP
          ? (() => {
              const paired: Set<string> = new Set()
              const teams: IGameUser[][] = []
              users.forEach((u) => {
                if (paired.has(u.uid)) return
                const partner = users.find(
                  (p) =>
                    p.uid === u.doubleUpPartnerId &&
                    u.doubleUpPartnerId !== "" &&
                    p.doubleUpPartnerId === u.uid &&
                    !paired.has(p.uid) // add this check
                )
                if (partner) {
                  teams.push([u, partner])
                  paired.add(u.uid)
                  paired.add(partner.uid)
                } else {
                  teams.push([u])
                }
              })
              return teams.map((team, teamIndex) => (
                <React.Fragment key={teamIndex}>
                  <div className="team-name">
                    <div
                      className="team-color-indicator"
                      style={{
                        backgroundColor: `var(--color-team${teamIndex + 1})`
                      }}
                    />
                    {t("team_name", { name: teamIndex + 1 })}
                  </div>
                  <div
                    key={team.map((u) => u.uid).join("-")}
                    className={`double-up-pair ${team.length === 2 ? "paired" : "unpaired"}`}
                  >
                    {team.map((u) => (
                      <PreparationMenuUser
                        key={u.uid}
                        user={u}
                        isOwner={isOwner}
                        ownerId={ownerId}
                      />
                    ))}
                  </div>
                </React.Fragment>
              ))
            })()
          : users.map((u) => (
              <PreparationMenuUser
                key={u.uid}
                user={u}
                isOwner={isOwner}
                ownerId={ownerId}
              />
            ))}
      </div>

      <div className="actions">
        <div>
          {roomNameInput}
          <div className="spacer" />
          {scribbleRule}
        </div>

        {(BOTS_ENABLED || isAdmin) && <div>{botControls}</div>}

        <div>
          {roomEloButton}
          {minMaxRanks}
          <div className="spacer" />
        </div>

        <div>
          {roomPrivateButton}
          {roomInfo}
          <div className="spacer" />
          {readyButton}
          {startGameButton}
        </div>
      </div>

      {isOwner && showBotSelectModal && (
        <BotSelectModal
          botsSelected={users.filter((u) => u.isBot).map((u) => u.uid)}
          close={() => setShowBotSelectModal(false)}
        />
      )}
    </div>
  )
}

export function RankSelect(props: {
  label: string
  value: EloRank
  onChange: (rank: EloRank) => void
}) {
  const { t } = useTranslation()
  return (
    <label>
      {props.label}
      <select
        value={props.value}
        onChange={(e) => props.onChange(e.target.value as EloRank)}
        style={{ marginLeft: "0.5em" }}
      >
        {Object.values(EloRank).map((rank) => (
          <option key={rank} value={rank}>
            {t(`elorank.${rank}`)} ({EloRankThreshold[rank]})
          </option>
        ))}
      </select>
    </label>
  )
}
